import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

import Grow from '../../../../components/grow';
import useOnClickOutside from '../../../../hooks/useOnClickOutside';
import { TechnicianIcon } from '../../../../assets/icons/TechnicianIcon';
import { DialogBody } from '../../../../stateManagement/reducers/confirmDialogReducer';
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog';
import TaskService from '../../../../services/TaskService';
import NotificationService from '../../../../services/NotificationService';
import { Container, ExpandedToolMenu, MenuTrigger } from '../../../../styling/ListToolsStyling';
import { linkToTask } from '../../task-details-view2/utils/LinkToWorkTask';

interface Props {
  selectedWorkTasks: { workTaskId: string; hasSubTasks: boolean; department?: string }[];
  updateTasks: () => void;
  setIsLoading: (isLoading: boolean) => void;
  worktaskHasDifferentDepartment: boolean;
}

const PlanningSystemMenu = (props: Props) => {
  const { selectedWorkTasks, updateTasks, setIsLoading, worktaskHasDifferentDepartment } = props;

  const [open, setOpen] = useState(false);

  const { getConfirmation } = useConfirmationDialog();
  const rootRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(rootRef, () => setOpen(false));

  const handlePlanWithSystem = useCallback(
    async (systemName: 'FLS' | 'D365') => {
      const workTaskIds = selectedWorkTasks.map((x) => +x.workTaskId);

      const dialogBody: DialogBody = {
        headerText:
          workTaskIds.length > 1
            ? `Vil du sende ${workTaskIds.length} opgaver og eventuelle underopgaver til ${systemName}?`
            : `Vil du sende opgaven og eventuelle underopgaver til ${systemName}?`,
        bodyText: worktaskHasDifferentDepartment ? 'Mindst én opgaverne er tildelt end anden afdeling din egen.' : '',
        declineButtonText: 'Fortryd',
        confirmButtonText: 'Bekræft'
      };

      const confirmation = await getConfirmation(dialogBody);

      if (confirmation === 'confirm') {
        setIsLoading(true);
        setOpen(false);

        const serviceCall = systemName === 'FLS' 
          ? TaskService.sendToPlanning(workTaskIds)
          : TaskService.planWithD365(workTaskIds);

        serviceCall
          .then(() => {
            updateTasks();

            NotificationService.showToast({
              severity: 'success',
              text:
                workTaskIds.length > 1
                  ? `Sendte ${workTaskIds.length} opgaver til ${systemName}. Med første opgave id: `
                  : `Opgave sendt til ${systemName} med id `,
              linkText: `${workTaskIds[0]}`,
              linkTo: linkToTask(workTaskIds[0].toString()),
              duration: 10000
            });
          })
          .catch((error) => {
            let errorMessage;
            switch (error.status) {
              case 400:
                errorMessage = 'Ugyldig status på en eller flere opgaver. Opdater din driftsliste.';
                break;
              case 404:
                errorMessage = `Opgavetypen findes ikke i ${systemName}.`;
                break;
              default:
                errorMessage = `Der opstod en fejl i afsendelsen af opgaver: ${error}`;
                break;
            }
            NotificationService.error(errorMessage);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setOpen(false);
      }
    },
    [selectedWorkTasks, updateTasks, setIsLoading, getConfirmation, worktaskHasDifferentDepartment]
  );

  return (
    <Container ref={rootRef}>
      <MenuTrigger onClick={() => setOpen(!open)} variant="primary" square data-testid="planning-system-button">
        <TechnicianIcon size="24px" />
        Planlæg med...
      </MenuTrigger>

      <Grow in={open}>
        <ExpandedToolMenu enableScroll={false}>
          <MenuItem onClick={() => handlePlanWithSystem('FLS')} data-testid="plan-with-fls">
            <strong>FLS</strong>
          </MenuItem>
          <MenuItem onClick={() => handlePlanWithSystem('D365')} data-testid="plan-with-d365">
            <strong>D365</strong>
          </MenuItem>
        </ExpandedToolMenu>
      </Grow>
    </Container>
  );
};

const MenuItem = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.main.yellow.primary};
  color: ${(props) => props.theme.palette.main.black.primary};
  border-radius: 8px;
  margin: 4px 0;

  &:hover {
    background-color: ${(props) => props.theme.palette.main.yellow.secondary};
  }
`;

export default PlanningSystemMenu;
